@switch (facade.state()) {
  @case ('error') {
    <section class="av-card p-4">
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.users.detail.error.title' | translate"
        [message]="'backoffice.users.detail.error.message' | translate"
      ></av-empty-state>
    </section>
  }
  @case ('ready') {
    @if (facade.user(); as user) {
      <section class="av-card p-4 d-flex flex-column gap-3">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div class="d-flex align-items-center gap-3">
            <av-avatar [name]="user.initialsSeed" size="lg"></av-avatar>

            <div class="d-flex flex-column gap-1">
              <span class="av-section-title d-flex align-items-center gap-2">
                {{ user.fullName }}

                @if (user.is_root) {
                  <av-status-chip
                    [label]="'backoffice.common.root_chip' | translate"
                    tone="info"
                  ></av-status-chip>
                }

                @if (user.isDeleted) {
                  <av-status-chip
                    [label]="'backoffice.users.detail.deleted_chip' | translate"
                    tone="danger"
                  ></av-status-chip>
                }
              </span>
              <span class="av-section-hint">{{ user.email || unresolved }}</span>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2">
            <a
              class="av-btn"
              [routerLink]="['/users']"
              [queryParams]="{ organization_id: organizationId() }"
            >
              <mat-icon aria-hidden="true">arrow_back</mat-icon>
              {{ 'backoffice.common.actions.back' | translate }}
            </a>

            @if (facade.canImpersonate()) {
              <button
                type="button"
                class="av-btn av-btn-primary"
                [matTooltip]="'backoffice.users.impersonation.scope_note' | translate"
                [disabled]="facade.isMutating()"
                (click)="openImpersonate()"
              >
                <mat-icon aria-hidden="true">switch_account</mat-icon>
                {{ 'backoffice.users.detail.impersonate' | translate }}
              </button>
            }
          </div>
        </div>

        <dl class="fact-grid m-0 d-grid gap-2">
          <dt class="av-meta m-0">
            {{ 'backoffice.users.detail.facts.organization' | translate }}
          </dt>
          <dd class="fact-value m-0">{{ user.organizationName }}</dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.core_role' | translate }}</dt>
          <dd class="fact-value m-0">{{ user.roleName }}</dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.owns_seat' | translate }}</dt>
          <dd class="fact-value m-0">
            {{
              (user.ownsCoreSeat ? 'backoffice.common.yes' : 'backoffice.common.no') | translate
            }}
          </dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.app_status' | translate }}</dt>
          <dd class="fact-value m-0">{{ user.appStatusLabelKey | translate }}</dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.licence' | translate }}</dt>
          <dd class="fact-value m-0">{{ user.licence_state || unresolved }}</dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.job_title' | translate }}</dt>
          <dd class="fact-value m-0">{{ user.job_title || unresolved }}</dd>

          <dt class="av-meta m-0">
            {{ 'backoffice.users.detail.facts.principal_name' | translate }}
          </dt>
          <dd class="fact-value m-0">{{ user.userPrincipalName || unresolved }}</dd>

          <dt class="av-meta m-0">
            {{ 'backoffice.users.detail.facts.entra_object_id' | translate }}
          </dt>
          <dd class="fact-value fact-mono m-0">{{ user.azure_user_id || unresolved }}</dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.office' | translate }}</dt>
          <dd class="fact-value m-0">{{ user.officeLocation || unresolved }}</dd>

          <dt class="av-meta m-0">{{ 'backoffice.users.detail.facts.created' | translate }}</dt>
          <dd class="fact-value m-0">
            {{ user.created_at ? (user.created_at | date: dateTimeFormat) : unresolved }}
          </dd>
        </dl>
      </section>

      <section class="av-card p-4 mt-3 d-flex flex-column gap-3">
        <div class="d-flex flex-column gap-1">
          <h2 class="av-section-title m-0">
            {{ 'backoffice.users.detail.role_permissions.title' | translate }}
          </h2>
          <p class="av-meta m-0">{{ 'backoffice.rbac.read_only_note' | translate }}</p>
        </div>

        @if (facade.rolePermissions().length === 0) {
          <av-empty-state
            icon="lock_open"
            [title]="'backoffice.users.detail.role_permissions.empty_title' | translate"
            [message]="'backoffice.users.detail.role_permissions.empty_message' | translate"
          ></av-empty-state>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100">
              <thead>
                <tr>
                  <th scope="col">
                    {{ 'backoffice.users.detail.columns.permission' | translate }}
                  </th>
                  <th scope="col">
                    {{ 'backoffice.users.detail.columns.scopeable' | translate }}
                  </th>
                  <th scope="col">
                    {{ 'backoffice.users.detail.columns.scope_on_role' | translate }}
                  </th>
                </tr>
              </thead>

              <tbody>
                @for (permission of facade.rolePermissions(); track permission.id) {
                  <tr>
                    <td class="cell-strong">{{ permission.displayName }}</td>
                    <td>
                      @if (permission.is_scopeable) {
                        <av-status-chip
                          [label]="'backoffice.users.detail.scopeable_yes' | translate"
                          tone="info"
                        ></av-status-chip>
                      } @else {
                        <av-status-chip
                          [label]="'backoffice.users.detail.scopeable_global' | translate"
                          tone="neutral"
                        ></av-status-chip>
                      }
                    </td>
                    <td class="cell-muted">{{ permission.scopeLabel }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <section class="av-card p-4 mt-3 d-flex flex-column gap-3">
        <div class="d-flex flex-column gap-1">
          <h2 class="av-section-title m-0">
            {{ 'backoffice.users.detail.scopes.title' | translate }}
          </h2>
          <p class="av-meta m-0">{{ 'backoffice.rbac.scope_source_note' | translate }}</p>
        </div>

        @if (facade.scopes().length === 0) {
          <av-empty-state
            icon="tune"
            [title]="'backoffice.users.detail.scopes.empty_title' | translate"
            [message]="'backoffice.users.detail.scopes.empty_message' | translate"
          ></av-empty-state>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100">
              <thead>
                <tr>
                  <th scope="col">
                    {{ 'backoffice.users.detail.columns.permission' | translate }}
                  </th>
                  <th scope="col">
                    {{ 'backoffice.users.detail.columns.scopeable' | translate }}
                  </th>
                  <th scope="col">{{ 'backoffice.users.detail.columns.scope' | translate }}</th>
                  <th scope="col">{{ 'backoffice.users.detail.columns.recorded' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (scope of facade.scopes(); track scope.permission_id) {
                  <tr>
                    <td class="cell-strong">{{ scope.displayName }}</td>
                    <td>
                      @if (scope.is_scopeable) {
                        <av-status-chip
                          [label]="'backoffice.users.detail.scopeable_yes' | translate"
                          tone="info"
                        ></av-status-chip>
                      } @else {
                        <av-status-chip
                          [label]="'backoffice.users.detail.scopeable_no' | translate"
                          tone="warning"
                        ></av-status-chip>
                      }
                    </td>
                    <td class="cell-muted fact-mono">{{ scope.scopeLabel }}</td>
                    <td class="cell-muted">
                      {{
                        scope.created_at ? (scope.created_at | date: dateTimeFormat) : unresolved
                      }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    }
  }
  @default {
    <section class="av-card p-4">
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.users.detail.loading.title' | translate"
        [message]="'backoffice.users.detail.loading.message' | translate"
      ></av-empty-state>
    </section>
  }
}
